import { CreditCard } from 'lucide-react';
import { Button } from '@/components/ui';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';

export type PaymentsConnectEmptyStateVariant =
  | 'starter'
  | 'setup'
  | 'club_setup'
  | 'complete'
  | 'region_unavailable';

const VARIANT_COPY: Record<
  Exclude<PaymentsConnectEmptyStateVariant, 'region_unavailable'>,
  { title: string; description: string; cta: string }
> = {
  starter: {
    title: 'Manual payment collection',
    description:
      'Your Starter plan collects lesson and play event payments outside CourtSync. Add payment instructions in Settings, mark play event fees on each event roster, and use the Billing Report to reconcile. Upgrade to Professional to accept online payments with Stripe Connect.',
    cta: 'View payment settings',
  },
  setup: {
    title: 'Set up Stripe Connect to accept online payments',
    description:
      'Connect your academy to Stripe to accept card payments, manage payouts, and unlock the payments dashboard, discounts, and accounting integrations.',
    cta: 'Go to Settings',
  },
  club_setup: {
    title: 'Set up Stripe Connect to accept online payments',
    description:
      'Connect your club to Stripe to accept court hire, membership, and play event payments online. Without Connect, mark play event fees on each event roster and reconcile them in Payments → Billing Report.',
    cta: 'Set up Stripe Connect',
  },
  complete: {
    title: 'Complete your Stripe setup',
    description:
      'Your Stripe Connect account is created but not fully activated yet. Finish onboarding in Settings to start accepting online payments.',
    cta: 'Complete setup in Settings',
  },
};

const REGION_UNAVAILABLE_DESCRIPTION =
  'Stripe Connect is not available for your country yet. You can still collect lesson and play event payments manually—add payment instructions in Settings, mark play event fees on each event roster, and use the Billing Report to reconcile.';

export interface PaymentsConnectEmptyStateProps {
  variant: PaymentsConnectEmptyStateVariant;
  onGoToSettings: () => void;
}

export function PaymentsConnectEmptyState({
  variant,
  onGoToSettings,
}: PaymentsConnectEmptyStateProps) {
  const { title, description, cta } =
    variant === 'region_unavailable'
      ? {
          title: 'Online payments not available in your region',
          description: REGION_UNAVAILABLE_DESCRIPTION,
          cta: 'View payment settings',
        }
      : VARIANT_COPY[variant];

  return (
    <DashboardEmptyStateCard className="mb-8">
      <DashboardEmptyStateIcon icon={CreditCard} />
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
      <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-lg mx-auto">{description}</p>
      <Button onClick={onGoToSettings}>{cta}</Button>
    </DashboardEmptyStateCard>
  );
}

export function resolvePaymentsConnectEmptyStateVariant(
  hasPaymentAccess: boolean,
  connectAccountId: string | null,
  lessonConnectSupported: boolean,
  options?: { clubPaymentsEnabled?: boolean }
): PaymentsConnectEmptyStateVariant {
  if (!lessonConnectSupported) return 'region_unavailable';
  if (!hasPaymentAccess && !options?.clubPaymentsEnabled) return 'starter';
  if (!connectAccountId) return options?.clubPaymentsEnabled ? 'club_setup' : 'setup';
  return 'complete';
}
